import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import { useCar } from '@/contexts/CarContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface SymptomRecurrenceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carId: string;
  issueId: string;
  issueName: string;
}

const SymptomRecurrenceForm: React.FC<SymptomRecurrenceFormProps> = ({
  open,
  onOpenChange,
  carId,
  issueId,
  issueName,
}) => {
  const { addRecurrence, updateIssue, cars } = useCar();
  const navigate = useNavigate();
  const [date, setDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Find the current issue and its symptoms
  const car = cars.find(c => c.id === carId);
  const issue = car?.issues?.find(i => i.id === issueId);

  const synthesizeSymptoms = async (notesText: string): Promise<string[]> => {
    if (!notesText || notesText.trim().length === 0) return [];
    
    try {
      // Detect language (simplified approach)
      const language = detectLanguage(notesText);
      
      // Call the OpenAI function to extract symptoms
      const { data, error } = await supabase.functions.invoke('synthesize-symptoms', {
        body: { 
          text: notesText,
          language 
        }
      });
      
      if (error) {
        console.error('Error synthesizing symptoms:', error);
        return [];
      }
      
      return data.result || [];
    } catch (err) {
      console.error('Error processing notes for symptoms:', err);
      return [];
    }
  };
  
  // Simple language detection based on character frequency
  const detectLanguage = (text: string): string => {
    // Latvian-specific characters
    const latvianChars = ['ā', 'č', 'ē', 'ģ', 'ī', 'ķ', 'ļ', 'ņ', 'š', 'ū', 'ž'];
    
    // Convert to lowercase for better matching
    const lowerText = text.toLowerCase();
    
    // Check if any Latvian characters are present
    const hasLatvianChars = latvianChars.some(char => lowerText.includes(char));
    
    return hasLatvianChars ? 'Latvian' : 'English';
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Add the recurrence to the issue
      addRecurrence(carId, issueId, {
        date: format(date, 'yyyy-MM-dd'),
        notes,
      });
      
      // Only proceed if we have notes and the issue exists
      if (notes.trim().length > 0 && issue) {
        // Extract potential symptoms from the notes using OpenAI
        const formattedSymptoms = await synthesizeSymptoms(notes);
        
        if (formattedSymptoms.length > 0) {
          // Update the issue with the new symptoms
          updateIssue(carId, issueId, {
            // Include new symptoms from the recurrence notes
            notes: issue.notes ? 
              `${issue.notes}\n\nAdditional symptoms from recurrence on ${format(date, 'PPP')}:\n${formattedSymptoms.join('\n')}` : 
              `Additional symptoms from recurrence on ${format(date, 'PPP')}:\n${formattedSymptoms.join('\n')}`
          });
        }
      }
      
      toast.success('Recurrence added successfully');
      
      // Close the dialog and navigate to the symptom summary
      onOpenChange(false);
      navigate(`/symptom-summary/${carId}/${issueId}`);
    } catch (error) {
      console.error('Error adding recurrence:', error);
      toast.error('Failed to add recurrence');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add symptom recurrence</DialogTitle>
          <DialogDescription>
            Record when this symptom occurred again: "{issueName}"
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          <div>
            <label className="text-sm font-medium mb-2 block">When did it happen again?</label>
            <div className="border rounded-md p-3 mb-4">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(date) => date && setDate(date)}
                className="p-3 pointer-events-auto"
                disabled={(date) => date > new Date()}
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Notes about this recurrence</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe the circumstances when this happened again..."
              className="min-h-[100px]"
            />
            <p className="text-xs text-gray-500 mt-1">
              Any new symptoms you describe here will be added to the symptom summary.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end gap-2 mt-4">
          <DialogClose asChild>
            <Button variant="outline" disabled={isSubmitting}>Cancel</Button>
          </DialogClose>
          <Button 
            className="bg-lime text-black hover:bg-lime/90" 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Recurrence'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SymptomRecurrenceForm;
